import React, { useState } from "react";
import { PieChartProps } from "../types/dashboard";
import { usePieChartData } from "../hooks/usePieChartData";
import NoDataMessage from "./parts/NoDataMessage";
import ChartContainer from "./parts/ChartContainer";
import DataSummary from "./parts/DataSummary";
import PieChartsHeader from "./parts/PieChartsHeader";

const PieCharts: React.FC<PieChartProps> = ({ Orders = 0, amount = 0, ReceivedAmount = 0, OutstandingAmount = 0, title = "Order Summary", timeRange = "today", businessType }) => {
  const { hasValidData, chartData, noDataMessage } = usePieChartData({ Orders, amount, ReceivedAmount, OutstandingAmount, timeRange, businessType });

  const [showDetails, setShowDetails] = useState(false);

  const handleToggleDetails = () => setShowDetails((prev) => !prev);

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (!hasValidData) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" className="text-[15px] font-bold [text-shadow:1px_1px_2px_rgba(0,0,0,0.8)]">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;

    // Enhanced tooltip with left accent
    return (
      <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200/80 shadow-xl p-4 min-w-[140px] backdrop-blur-sm">
        {/* Left accent bar */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-r-full" style={{ backgroundColor: data.fill }} />
        <p className="font-bold text-sm text-gray-900 mb-1">{data.name}</p>
        <p className="text-lg font-semibold" style={{ color: data.fill }}>
          {data.name === "Orders" ? `${data.value} orders` : `QAR ${data.value.toLocaleString()}`}
        </p>
      </div>
    );
  };

  return (
    <div className="group relative rounded-2xl bg-gradient-to-br from-white to-gray-50/80 border border-gray-200/60 shadow-xl transition-all duration-500 h-full flex flex-col overflow-hidden">
      {/* Enhanced left accent border */}
      {/* <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 shadow-lg"></div> */}

      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/30 to-transparent opacity-50"></div>
      <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/5 rounded-full -translate-y-10 translate-x-10"></div>
      <div className="absolute bottom-0 left-10 w-16 h-16 bg-purple-500/5 rounded-full translate-y-8 -translate-x-8"></div>

      <div className="relative z-10 flex-1 flex flex-col p-6">
        {/* Header with enhanced design */}
        <PieChartsHeader title={title} onToggleDetails={handleToggleDetails} showDetails={showDetails} />

        {/* Chart Container with enhanced styling */}
        <div className="min-h-[230px] h-[230px] relative mb-2">
          {!hasValidData && <NoDataMessage message={noDataMessage} />}
          <ChartContainer hasValidData={hasValidData} chartData={chartData} renderCustomizedLabel={renderCustomizedLabel} CustomTooltip={CustomTooltip} />
        </div>

        {/* Enhanced Data Summary with smooth transitions */}
        <div className={`overflow-hidden transition-all duration-700 ease-in-out ${showDetails ? "max-h-[500px] opacity-100 mt-4 pb-2" : "max-h-0 opacity-0"}`}>
          <div className="border-t border-gray-200/60 pt-4">
            <DataSummary chartData={chartData} businessType={businessType} />
          </div>
        </div>

        {/* Enhanced footer indicator */}
        <div className="mt-2 pt-2 border-t border-gray-200/40">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-gray-600 font-medium">Live Data</span>
            </div>
            <div className="text-gray-500 flex items-center space-x-1">
              <span>📊</span>
              <span>Interactive Chart</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hover effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-100 transition-opacity duration-500 rounded-2xl"></div>
    </div>
  );
};

export default PieCharts;
